import * as React from 'react';
import './PageHeader.css';

/**
 * PageHeader (CC2): the one page-title block, in the landing's language.
 *
 *   <PageHeader eyebrow="Your workspace" title="Settings"
 *     description="Profile, coaching style, workspace and billing."
 *     actions={<Button>New scenario</Button>} />
 *
 * eyebrow      11px / 0.14em uppercase teal-700 kicker with a leading dash
 * title        Montserrat 800, -0.02em, dark ink (h1 by default; `as` for
 *              pages whose h1 lives elsewhere)
 * description  optional one-line subtitle, text-secondary
 * actions      optional right-hand slot (buttons, a segmented control)
 * breadcrumb   optional node rendered above the eyebrow (plain text or a
 *              back link; the component does not style routes)
 * align        'start' (default) or 'center': the centred variant stacks the
 *              kicker, title, subtitle and actions on the page axis (onboarding
 *              steps), so pages stop re-centring the inner blocks with CSS
 */
export function PageHeader({
  eyebrow,
  title,
  description,
  actions,
  breadcrumb,
  as: Tag = 'h1',
  align = 'start',
  className = '',
  children,
}: {
  eyebrow?: React.ReactNode;
  title: React.ReactNode;
  description?: React.ReactNode;
  actions?: React.ReactNode;
  breadcrumb?: React.ReactNode;
  as?: 'h1' | 'h2';
  align?: 'start' | 'center';
  className?: string;
  children?: React.ReactNode;
}) {
  const cls = ['lnr-page-header', align === 'center' ? 'lnr-page-header--center' : '', className]
    .filter(Boolean)
    .join(' ');
  return (
    <header className={cls}>
      <div className="lnr-page-header__text">
        {breadcrumb ? <div className="lnr-page-header__crumb">{breadcrumb}</div> : null}
        {eyebrow ? <div className="lnr-page-header__eyebrow">{eyebrow}</div> : null}
        <Tag className="lnr-page-header__title">{title}</Tag>
        {description ? <p className="lnr-page-header__desc">{description}</p> : null}
        {children}
      </div>
      {actions ? <div className="lnr-page-header__actions">{actions}</div> : null}
    </header>
  );
}

export default PageHeader;
